import { formatearMoneda } from '@barbones/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';
import { fecha } from '@/lib/utils';

export interface LineaCompraVista {
  id: string;
  productoId: string;
  cantidad: number;
  costoUnitario: number;
  paquetes: number | null;
  costoPaquete: number | null;
  aVenta: number;
  aUso: number;
  producto: { nombre: string; piezasPorPaquete: number | null; eliminadoEn: string | null } | null;
}

export interface CompraVista {
  id: string;
  /** Día de negocio guardado como fecha a medianoche UTC. */
  fecha: string;
  total: number;
  notas: string | null;
  proveedor: { nombre: string } | null;
  lineas: LineaCompraVista[];
}

const cent = (t: string) => Math.round((Number.parseFloat(t) || 0) * 100);
const diaCompra = (c: CompraVista) => fecha(`${c.fecha.slice(0, 10)}T12:00:00`);
const totalLinea = (l: Pick<LineaCompraVista, 'cantidad' | 'costoUnitario' | 'paquetes' | 'costoPaquete'>) =>
  l.paquetes !== null && l.costoPaquete !== null ? l.paquetes * l.costoPaquete : l.cantidad * l.costoUnitario;
const resumenLinea = (l: LineaCompraVista) => `${l.producto?.nombre ?? 'Producto'} × ${l.cantidad}${l.paquetes ? ` (${l.paquetes} paq.)` : ''}`;

export function TablaCompras({ compras, onCorregir }: { compras: CompraVista[]; onCorregir: (c: CompraVista) => void }) {
  const { puede } = useSesion();
  const corregible = puede('inventario.editar') && puede('boveda.retirar');
  return (
    <Tabla>
      <thead>
        <tr>
          <Th>Fecha</Th>
          <Th>Proveedor</Th>
          <Th>Productos</Th>
          <Th className="text-right">Total</Th>
          {corregible && <Th />}
        </tr>
      </thead>
      <tbody>
        {compras.map((c) => (
          <Tr key={c.id}>
            <Td className="cifras whitespace-nowrap">{diaCompra(c)}</Td>
            <Td>{c.proveedor?.nombre ?? '—'}</Td>
            <Td>
              {c.lineas.map((l) => (
                <span key={l.id} className="block">
                  {resumenLinea(l)}
                </span>
              ))}
            </Td>
            <Td className="cifras text-right font-medium">{formatearMoneda(c.total)}</Td>
            {corregible && (
              <Td className="text-right">
                <Button variante="secundario" onClick={() => onCorregir(c)}>
                  Corregir
                </Button>
              </Td>
            )}
          </Tr>
        ))}
      </tbody>
    </Tabla>
  );
}

interface Borrador {
  id: string;
  /** Piezas por paquete actuales del producto; null = se corrige por pieza. */
  porPaquete: number | null;
  bloqueada: boolean;
  /** Sin tocar: se envía tal como está guardada (aunque el producto haya cambiado de modo). */
  editada: boolean;
  unidades: string;
  costo: string;
  aVenta: string;
  aUso: string;
}

/** Cada línea se corrige en el modo actual del producto, igual que una compra nueva. */
function aBorrador(l: LineaCompraVista): Borrador {
  const porPaquete = l.producto?.piezasPorPaquete ?? null;
  const base = { id: l.id, porPaquete, bloqueada: Boolean(l.producto?.eliminadoEn), editada: false, aVenta: String(l.aVenta), aUso: String(l.aUso) };
  if (porPaquete) {
    const paquetes = l.paquetes ?? (l.cantidad % porPaquete === 0 ? l.cantidad / porPaquete : null);
    return { ...base, unidades: paquetes ? String(paquetes) : '', costo: l.costoPaquete !== null ? String(l.costoPaquete / 100) : '' };
  }
  return { ...base, unidades: String(l.cantidad), costo: String(l.costoUnitario / 100) };
}

const entero = (t: string) => (/^\d+$/.test(t.trim()) ? Number(t) : 0);
const piezasDe = (b: Borrador) => entero(b.unidades) * (b.porPaquete ?? 1);
const totalDe = (b: Borrador) => entero(b.unidades) * cent(b.costo);

export function DialogoCorregirCompra({ compra, onCerrar }: { compra: CompraVista; onCerrar: () => void }) {
  const qc = useQueryClient();
  const [borradores, setBorradores] = useState(() => compra.lineas.map(aBorrador));
  const cambiar = (id: string, campo: keyof Pick<Borrador, 'unidades' | 'costo' | 'aVenta' | 'aUso'>, valor: string) =>
    setBorradores((bs) => bs.map((b) => (b.id === id ? { ...b, [campo]: valor, editada: true } : b)));

  const lineaDe = (id: string) => compra.lineas.find((l) => l.id === id)!;
  const intacta = (b: Borrador) => b.bloqueada || !b.editada;
  const totalNuevo = borradores.reduce((s, b) => s + (intacta(b) ? totalLinea(lineaDe(b.id)) : totalDe(b)), 0);
  const diferencia = compra.total - totalNuevo;
  const errores = new Map(
    borradores
      .filter((b) => !intacta(b))
      .map((b) => {
        if (!entero(b.unidades) || cent(b.costo) < 1) return [b.id, b.porPaquete ? 'Indica paquetes y costo por paquete' : 'Indica piezas y costo por pieza'] as const;
        if (entero(b.aVenta) + entero(b.aUso) !== piezasDe(b)) return [b.id, `Venta + uso debe sumar ${piezasDe(b)} piezas`] as const;
        return [b.id, null] as const;
      })
      .filter(([, e]) => e !== null),
  );

  const guardar = useMutation({
    mutationFn: () =>
      api(`/compras/${compra.id}`, {
        method: 'PATCH',
        body: {
          lineas: borradores.map((b) => {
            const original = lineaDe(b.id);
            if (intacta(b)) {
              const modo = original.paquetes !== null ? { paquetes: original.paquetes, costoPaquete: original.costoPaquete } : { cantidad: original.cantidad, costoUnitario: original.costoUnitario };
              return { id: b.id, ...modo, aVenta: original.aVenta, aUso: original.aUso };
            }
            const modo = b.porPaquete ? { paquetes: entero(b.unidades), costoPaquete: cent(b.costo) } : { cantidad: entero(b.unidades), costoUnitario: cent(b.costo) };
            return { id: b.id, ...modo, aVenta: entero(b.aVenta), aUso: entero(b.aUso) };
          }),
        },
      }),
    onSuccess: () => {
      toast.success('Compra corregida');
      void qc.invalidateQueries({ queryKey: ['compras'] });
      void qc.invalidateQueries({ queryKey: ['productos'] });
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });

  return (
    <Dialogo
      abierto
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo="Corregir compra"
      descripcion={`${diaCompra(compra)}${compra.proveedor ? ` · ${compra.proveedor.nombre}` : ''}`}
      pie={
        <Button onClick={() => guardar.mutate()} cargando={guardar.isPending} disabled={errores.size > 0}>
          Guardar corrección
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {borradores.map((b) => {
          const linea = lineaDe(b.id);
          const error = errores.get(b.id);
          return (
            <fieldset key={b.id} className="rounded-lg border border-border p-3" disabled={b.bloqueada}>
              <legend className="px-1 text-[13.5px] font-medium">{linea.producto?.nombre ?? 'Producto'}</legend>
              {b.bloqueada && <p className="mb-2 text-[12.5px] text-foreground-muted">El producto fue eliminado; esta línea no se puede corregir.</p>}
              <div className="grid grid-cols-2 gap-3">
                <Campo etiqueta={b.porPaquete ? `Paquetes (de ${b.porPaquete} piezas)` : 'Piezas'}>
                  {(p) => <Input {...p} type="number" min="1" inputMode="numeric" value={b.unidades} onChange={(e) => cambiar(b.id, 'unidades', e.target.value)} className="cifras" />}
                </Campo>
                <Campo etiqueta={b.porPaquete ? 'Costo por paquete' : 'Costo por pieza'}>
                  {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={b.costo} onChange={(e) => cambiar(b.id, 'costo', e.target.value)} className="cifras" />}
                </Campo>
                <Campo etiqueta="Para venta">
                  {(p) => <Input {...p} type="number" min="0" inputMode="numeric" value={b.aVenta} onChange={(e) => cambiar(b.id, 'aVenta', e.target.value)} className="cifras" />}
                </Campo>
                <Campo etiqueta="Uso interno">
                  {(p) => <Input {...p} type="number" min="0" inputMode="numeric" value={b.aUso} onChange={(e) => cambiar(b.id, 'aUso', e.target.value)} className="cifras" />}
                </Campo>
              </div>
              {error && <p className="mt-2 text-[12.5px] text-warning">{error}</p>}
            </fieldset>
          );
        })}
        <dl className="cifras grid grid-cols-2 gap-y-1.5 text-[13.5px]">
          <dt className="text-foreground-muted">Total antes</dt>
          <dd className="text-right">{formatearMoneda(compra.total)}</dd>
          <dt className="text-foreground-muted">Total nuevo</dt>
          <dd className="text-right font-semibold">{formatearMoneda(totalNuevo)}</dd>
        </dl>
        {diferencia !== 0 && errores.size === 0 && (
          <p className="text-[12.5px] text-foreground-muted">
            Si la compra se pagó desde la bóveda, {diferencia > 0 ? `regresan ${formatearMoneda(diferencia)} a la bóveda` : `salen ${formatearMoneda(-diferencia)} de la bóveda`}. El gasto queda en {formatearMoneda(totalNuevo)}.
          </p>
        )}
        <p className="text-[12.5px] text-foreground-muted">No cambia el costo ni el precio del producto; para eso está «Cambiar costo».</p>
      </div>
    </Dialogo>
  );
}

export function ComprasPage() {
  const compras = useQuery({ queryKey: ['compras'], queryFn: () => api<CompraVista[]>('/compras') });
  const [corrigiendo, setCorrigiendo] = useState<CompraVista | null>(null);
  return (
    <ModuleLayout titulo="Inventario" secciones={SECCIONES_POR_MODULO.inventario}>
      <EncabezadoPagina titulo="Compras" descripcion="Historial de compras de productos. Corrige una compra mal capturada sin descuadrar el gasto, la bóveda ni el stock." />
      <Tarjeta>
        {compras.isLoading ? (
          <Esqueleto className="m-4 h-40" />
        ) : !compras.data?.length ? (
          <Vacio icono={<Receipt />} titulo="Sin compras" descripcion="Las compras se registran al resurtir un producto." />
        ) : (
          <TablaCompras compras={compras.data} onCorregir={setCorrigiendo} />
        )}
      </Tarjeta>
      {corrigiendo && <DialogoCorregirCompra compra={corrigiendo} onCerrar={() => setCorrigiendo(null)} />}
    </ModuleLayout>
  );
}
